import { useCallback, useRef } from 'react'

import useRecommendationsAPI from './useRecommendationsAPI'
import { Product } from '../../utils/types/Product'
import { VertexAIUserEventType } from '../../utils/constants'
import useLocalStorageViewedProducts from '../../components/hooks/useLocalStorageViewedProducts'
import { useCart } from '../../components/Cart/CartProvider'

interface UseOthersYouMayLikeParams {
  cartId?: string
  defaultEventType?: VertexAIUserEventType
  pageSize?: number
  associatedItems?: Array<string | [string, number]>
  categoryFilter?: string
}

interface UseOthersYouMayLikeResult {
  data: Product[] | undefined
  isLoading: boolean
  error: Error | undefined
  isInitialCartLoaded: boolean
  triggerAddToCart: (skus: [string, number][]) => void
  triggerDetailPageView: (skus: string[]) => void
  triggerHomePageView: (skus?: string[]) => void
}

/**
 * Used to recommend COMPLIMENTARY products.
 * Prioritizes current session, less weight on past training.
 * Provides highly relevant recommendations but struggles with filtering.
 *
 * @param params Parameters for the hook
 * @returns Result containing recommended products and functions to trigger events
 * Products will only load when a trigger event gets called.
 */
const useOthersYouMayLike = (
  params: UseOthersYouMayLikeParams = {},
): UseOthersYouMayLikeResult => {
  const {
    cartId,
    defaultEventType = 'home-page-view',
    pageSize = 100,
    associatedItems = [],
    categoryFilter,
  } = params

  const { viewedSkus, isLoading: isViewedSkusLoading } = useLocalStorageViewedProducts()
  const { cartState: { isLoading: isCartLoading } } = useCart()
  const hasInitialCartLoaded = useRef(false)

  // Update hasInitialCartLoaded ref when cart is no longer loading
  if (!isCartLoading && !hasInitialCartLoaded.current) {
    hasInitialCartLoaded.current = true
  }

  const modelType = 'others-you-may-like'

  const {
    data,
    isLoading: isRecommendationsLoading,
    error: recommendationsError,
    triggerEvent,
  } = useRecommendationsAPI({
    modelType,
    cartId,
    associatedItems,
    defaultEventType,
    pageSize,
    categoryFilter,
  })

  const triggerAddToCart = useCallback((skus: [string, number][]) => {
    triggerEvent('add-to-cart', {
      associatedItems: skus,
      modelType,
    })
  }, [triggerEvent, modelType])

  const triggerDetailPageView = useCallback((productSkus: string[] = []) => {
    const skus = [...productSkus, ...viewedSkus]
    triggerEvent('detail-page-view', {
      associatedItems: skus,
      modelType,
    })
  }, [triggerEvent, modelType])

  const triggerHomePageView = useCallback((productSkus: string[] = []) => {
    const skus = [...productSkus, ...viewedSkus]
    triggerEvent('home-page-view', {
      associatedItems: skus,
      modelType,
    })
  }, [triggerEvent, modelType])

  return {
    data,
    isLoading: isRecommendationsLoading || isViewedSkusLoading,
    error: recommendationsError,
    isInitialCartLoaded: hasInitialCartLoaded.current,
    triggerAddToCart,
    triggerDetailPageView,
    triggerHomePageView,
  }
}

export default useOthersYouMayLike
